import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const PAGE_SIZE = 24;

export function ArtistsList() {
  const [artists, setArtists] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api.get("/artists", { params: { skip: 0, limit: PAGE_SIZE } }).then((r) => {
      setArtists(r.data.items);
      setTotal(r.data.total);
      setHasMore(r.data.has_more);
    }).finally(() => setLoading(false));
  }, []);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/artists", { params: { skip: artists.length, limit: PAGE_SIZE } });
      setArtists((prev) => [...prev, ...data.items]);
      setHasMore(data.has_more);
    } finally { setLoadingMore(false); }
  }, [artists.length]);

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10" data-testid="artists-page">
      <div className="border-b border-white/10 pb-6 mb-8 flex items-baseline justify-between">
        <h1 className="font-display text-6xl md:text-8xl uppercase">Pilotes</h1>
        <div className="text-xs uppercase tracking-widest text-white/40">{total} pilote{total > 1 ? "s" : ""}</div>
      </div>
      {loading ? (
        <div className="text-center py-20 text-white/50 uppercase tracking-widest">Chargement...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {artists.map((a) => (
              <Link key={a.id} to={`/pilotes/${a.id}`} className="border border-white/10 hover:border-[#FF3B00] p-6 transition-colors relative">
                {a.featured && (
                  <div className="absolute top-2 right-2 bg-[#FF3B00] text-white text-[9px] uppercase tracking-widest px-2 py-0.5">Vedette</div>
                )}
                <div className="aspect-square bg-[#111] mb-4 overflow-hidden flex items-center justify-center font-display text-6xl">
                  {a.avatar_url ? (
                    <img src={a.avatar_url} alt={a.artist_name || a.name} className="w-full h-full object-cover" />
                  ) : (
                    (a.artist_name || a.name || "?")[0].toUpperCase()
                  )}
                </div>
                <div className="font-display text-2xl uppercase truncate">{a.name || a.artist_name}</div>
                {a.artist_name && a.name && a.artist_name.toLowerCase() !== a.name.toLowerCase() && (
                  <div className="text-xs text-[#FF3B00] uppercase tracking-widest mt-1 truncate">« {a.artist_name} »</div>
                )}
                <div className="text-xs uppercase tracking-widest text-white/40 mt-2">{a.product_count} produits</div>
                {a.bio && <p className="text-sm text-white/60 mt-3 line-clamp-3">{a.bio}</p>}
              </Link>
            ))}
          </div>
          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button onClick={loadMore} disabled={loadingMore} data-testid="artists-load-more"
                className="px-8 py-4 border border-white/20 hover:border-[#FF3B00] hover:text-[#FF3B00] uppercase tracking-widest text-sm disabled:opacity-50">
                {loadingMore ? "Chargement..." : `Charger plus (${artists.length} / ${total})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ArtistDetail() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api.get(`/artists/${id}`).then((r) => setArtist(r.data));
    api.get("/products", { params: { artist_id: id, skip: 0, limit: PAGE_SIZE } }).then((r) => {
      setProducts(r.data.items);
      setTotal(r.data.total);
      setHasMore(r.data.has_more);
    });
  }, [id]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/products", { params: { artist_id: id, skip: products.length, limit: PAGE_SIZE } });
      setProducts((prev) => [...prev, ...data.items]);
      setHasMore(data.has_more);
    } finally { setLoadingMore(false); }
  };

  if (!artist) return <div className="p-20 text-center text-white/50 uppercase tracking-widest">Chargement...</div>;

  return (
    <div className="max-w-[1600px] mx-auto" data-testid="artist-page">
      <section className="px-6 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <div className="flex gap-6 items-start flex-wrap">
          <div className="w-32 h-32 md:w-40 md:h-40 bg-[#111] overflow-hidden flex items-center justify-center font-display text-6xl flex-shrink-0">
            {artist.avatar_url ? (
              <img src={artist.avatar_url} alt={artist.artist_name || artist.name} className="w-full h-full object-cover" />
            ) : (
              (artist.artist_name || artist.name || "?")[0].toUpperCase()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Pilote</div>
            <h1 className="font-display text-5xl md:text-8xl uppercase leading-none mt-2 break-words">{artist.name || artist.artist_name}</h1>
            {artist.artist_name && artist.name && artist.artist_name.toLowerCase() !== artist.name.toLowerCase() && (
              <div className="mt-3 text-[#FF3B00] uppercase tracking-[0.3em] text-sm md:text-base">« {artist.artist_name} »</div>
            )}
            {artist.bio && <p className="mt-6 max-w-2xl text-white/70">{artist.bio}</p>}
          </div>
        </div>
      </section>
      <section className="px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-widest text-white/40 mb-6">{total} produit{total > 1 ? "s" : ""}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
        {hasMore && (
          <div className="mt-8 flex justify-center">
            <button onClick={loadMore} disabled={loadingMore}
              className="px-8 py-4 border border-white/20 hover:border-[#FF3B00] hover:text-[#FF3B00] uppercase tracking-widest text-sm disabled:opacity-50">
              {loadingMore ? "..." : `Charger plus (${products.length} / ${total})`}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
